import { installedIds } from "@p0security/shared/install/installed";
import type { NewConfig } from "@p0security/shared/integrations/shared";
import {
  type McpServerSpec,
  propagationHint,
} from "@p0security/shared/types/agentic/mcp";

import { ACCOUNT_IDENTIFIER_PATTERN } from "./account-identifier.ts";
import { SnowflakeAiObjects } from "./constants.ts";
import { SnowflakeAiIconUrl } from "./icon.ts";
import { snowflakeAiMcpTools } from "./mcp-tools.ts";
import type { SnowflakeAiIntegration } from "./types.ts";

// TODO(INT-617): placeholder, timings measured on the dev account decide
// these.
const snowflakeAiPropagationHint = propagationHint({
  resource: "Snowflake",
  normalWait: "5 to 15 seconds",
  maxWait: "1 minute",
  pollInterval: "5 seconds",
});

/** The host of an account's URL: the account identifier, lower-cased, with
 * `_` replaced by `-`, which a host name can't hold. */
export const snowflakeAiAccountHost = (accountId: string) =>
  `${accountId.toLowerCase().replace(/_/g, "-")}.snowflakecomputing.com`;

/** The URL of an account's managed MCP server. The database, schema and
 * server names in the path stay as created. */
export const snowflakeAiMcpUrl = (accountId: string) =>
  `https://${snowflakeAiAccountHost(accountId)}/api/v2/databases/${SnowflakeAiObjects.database}/schemas/${SnowflakeAiObjects.schema}/mcp-servers/${SnowflakeAiObjects.mcpServer}`;

const prefixArgs = ["snowflake-ai"];

const accessPrompt = (
  accountId: string
) => `Request access to the Snowflake account ${accountId}. Each grant gets its
own Snowflake user, which holds what the grant names, and runs SQL on the
warehouse it names by default. Every request needs a duration of at most
365 days: one without an end time, or a longer one, is refused.

Pass the access type, then the argv for it.
`;

const commonArgs = `  - warehouse: The warehouse to run SQL on (e.g. 'COMPUTE_WH')`;

const durationArgs = `  - duration: How long access lasts, at most 365 days, as a number and
    one of minutes, hours, days or weeks, e.g. '2 hours', '5 days' or
    '1 week'
  - reason: The reason access is needed`;

const warehouseField = {
  description: "Snowflake warehouse",
  required: [],
};

const roleField = { description: "Snowflake role", required: [] };

const grantField = { description: "PRIVILEGE:OBJECT entry", required: [] };

/** The MCP server for one installed Snowflake account, by its item id.
 *
 * The account is pinned: every request and listing carries it, after the
 * access type, so an agent can only request access to the account its server
 * reaches. The URL is built from the item id, which is the account
 * identifier.
 */
export const snowflakeAiMcp = (
  accountId: string
): McpServerSpec & { type: "external" } => ({
  type: "external",
  label: `Snowflake (${accountId})`,
  iconUrl: SnowflakeAiIconUrl,
  credential: {
    type: "vault",
    // Each grant stages its own secret name; read that rather than rebuilding
    // the naming scheme here.
    path: "{{ request.generated.secretName }}",
  },
  tools: { type: "manual", tools: snowflakeAiMcpTools },
  // The minted user's grants decide what Snowflake allows.
  authorization: "upstream",
  url: snowflakeAiMcpUrl(accountId),
  access: {
    prompt: accessPrompt(accountId),
    prefixArgs,
    propagationHint: snowflakeAiPropagationHint,
    accessTypes: {
      role: {
        prompt: `For access_type "role", call with argv:

  [<warehouse>, <role>..., "--duration", <duration>, "--reason", <reason>]

where:

${commonArgs}
  - role: One or more existing Snowflake roles. Use the list tool to enumerate them.
${durationArgs}
`,
        args: ["role", accountId],
      },
      objects: {
        prompt: `For access_type "objects", call with argv:

  [<warehouse>, <grant>..., "--duration", <duration>, "--reason", <reason>]

where:

${commonArgs}
  - grant: One or more PRIVILEGE:OBJECT entries. PRIVILEGE is SELECT, INSERT,
    UPDATE or DELETE. OBJECT is a database (DB), a schema (DB.SCHEMA), or a
    table or view (DB.SCHEMA.OBJECT); a database or schema covers the tables
    and views in it when access is granted. Request only what your queries
    need. Use the list tool to enumerate them.
${durationArgs}
`,
        args: ["objects", accountId],
      },
    },
  },
  list: {
    prefixArgs,
    fields: {
      warehouse: warehouseField,
      name: roleField,
      grants: grantField,
    },
    accessTypes: {
      role: {
        args: ["role", "--account", accountId],
        fields: { warehouse: warehouseField, name: roleField },
      },
      objects: {
        args: ["objects", "--account", accountId],
        fields: { warehouse: warehouseField, grants: grantField },
      },
    },
  },
});

/** One Snowflake MCP server per installed account, for the agentic catalog's
 * `snowflake` family. Its type is `McpServerFamily` in
 * `@p0security/integrations-agentic-shared/mcp/registry`, given by shape,
 * since that package depends on this one.
 *
 * The item id comes from a stored server's id, and becomes the host the
 * gateway sends the agent's token to, so one that isn't an account
 * identifier names no server.
 */
export const snowflakeAiMcpServerFamily = {
  items: (installed: Record<string, NewConfig>): string[] =>
    installedIds(
      "credential",
      (installed["snowflake-ai"] ?? {}) as SnowflakeAiIntegration
    ),
  spec: (accountId: string) =>
    ACCOUNT_IDENTIFIER_PATTERN.test(accountId)
      ? snowflakeAiMcp(accountId)
      : undefined,
};
